import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { NEEDS_CONNECTION } from '../../utilities/localFirst/opQueue';

/**
 * Whether the browser is offline (`dashboard.isOnline`, kept by watchConnection). The food controls
 * whose writes the server has to compute (docs/local-first-food-data.md, "Online-only barrier") are
 * disabled then, rather than fail once pressed.
 */
export default function useOffline() {
	return useSelector((state) => !!state.dashboard && state.dashboard.isOnline === false);
}

/** The title an online-only control has while it is disabled offline. */
export const NEEDS_CONNECTION_TITLE = NEEDS_CONNECTION;

/**
 * Props for an online-only control: `props` as they are online, and offline the same disabled, with
 * the title "Needs a connection".
 */
export const onlineOnly = (offline, props = {}) => (offline ? { ...props, disabled: true, title: NEEDS_CONNECTION_TITLE } : props);

/**
 * Props for an online-only text field: offline it is read-only rather than disabled, titled "Needs a
 * connection". Disabling a field someone is typing in takes its focus, and a field that saves when
 * left would then save the half-typed text at once, while offline.
 */
export const onlineOnlyField = (offline, props = {}) => (offline ? { ...props, readOnly: true, title: NEEDS_CONNECTION_TITLE } : props);

/**
 * For fields that save when left: `whenOnline(key, save)` saves at once while online. Offline it holds
 * the save, the latest for each key, and makes it when the connection returns, so what was typed
 * before the connection dropped is neither sent offline nor lost. Saves held when the component goes
 * are dropped.
 */
export function useSaveWhenOnline(offline) {
	const held = useRef(new Map());
	useEffect(() => {
		if(offline || !held.current.size) {
			return;
		}
		const saves = [...held.current.values()];
		held.current.clear();
		saves.forEach((save) => save());
	}, [offline]);
	return useCallback((key, save) => {
		if(offline) {
			held.current.set(key, save);
			return;
		}
		held.current.delete(key);
		save();
	}, [offline]);
}
